import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { HeaderTool } from './styles';
import { launchAsync } from '@microsoft/immersive-reader-sdk';
import ImmersiveReaderContentBuilder from '../../../app/assets/js/shared/immersiveReader.js';
import ImmersiveReaderIcon from '../../../public/assets/images/theme-2/icons/panel-header/immersive-reader.svg';
import ImmersiveReaderIconWhite from '../../../public/assets/images/theme-2/icons/panel-header/immersive-reader-white.svg';

import useApi from '../../hooks/useApi';

export const IMMERSIVE_READER_CONTENT_CLASS = 'ir-content';

const ImmersiveReaderButton = ({
  userToken = null,
  nodeData = null,
  title = '',
  show = true,
  darkMode = false,
  className = 'irButton',
  inActivity = true,
}) => {
  const [launchParameters, setLaunchParameters] = useState(null);
  const domRef = useRef(null);

  const handleOpenEvent = (event) => {
    const text = event && event.detail;
    openImmersiveReader({
      title: i18n.__('app_selection'),
      chunks: [{ content: text, mimeType: 'text/html' }],
    });
  };

  useEffect(() => {
    document.addEventListener('open.immersiveReader', handleOpenEvent);
    return () => document.removeEventListener('open.immersiveReader', handleOpenEvent);
  }, []);

  const { getData } = useApi(userToken);
  const immersiveReaderContent = new ImmersiveReaderContentBuilder(title);

  const getLaunchParameters = async () => {
    if (launchParameters === null) {
      const response = await getData(`/integrations/microsoft/fetchImmersiveReaderLaunchParameters`);
      if (response.success) {
        setLaunchParameters(response.body);
        return response.body;
      }
    }
    return launchParameters;
  };

  const typeMap = {
    circle: 'loop',
    private_blog: 'blog',
    public_blog: 'blog',
    group: 'task',
    written_quiz: 'quiz',
  };

  const getNodeType = () => {
    const type = nodeData.type;
    if (!type) {
      return 'node';
    } else if (typeMap[type]) {
      return typeMap[type];
    } else {
      return type;
    }
  };

  const openImmersiveReader = async (selectedContent = undefined) => {
    let content = {};
    if (selectedContent) {
      content = selectedContent;
    } else if (nodeData) {
      immersiveReaderContent.buildFromContent(nodeData, getNodeType());
      content = immersiveReaderContent.getContent();
    } else {
      const parent = domRef.current.closest(`.${IMMERSIVE_READER_CONTENT_CLASS}`);
      if (!parent) {
        return;
      }
      const dom = parent.cloneNode(true);

      [...dom.querySelectorAll('.ir-hide, p')]
        .filter(
          (element) =>
            element.classList.contains('ir-hide') ||
            (element.children.length === 1 && element.children[0].tagName === 'BR'),
        )
        .forEach((element) => element.remove());

      let contentTitle = title;
      const titleDom = dom.querySelector('.ir-title');
      if (titleDom) {
        contentTitle = titleDom.innerText;
      }

      content = {
        title: contentTitle,
        chunks: [
          {
            content: dom.innerHTML,
            mimeType: 'text/html',
          },
        ],
      };
    }

    let options = {};
    try {
      const [language] = i18n.getLocale().split('-');
      if (language) {
        options.uiLang = language;
      }
    } catch (error) {}

    const { token, subdomain } = await getLaunchParameters();
    launchAsync(token, subdomain, content, options);
  };

  return (
    <HeaderTool
      src={darkMode ? ImmersiveReaderIconWhite : ImmersiveReaderIcon}
      onClick={() => {
        openImmersiveReader();
      }}
      role="img"
      title={i18n.__('app_open_screen_reader')}
      show={show}
      ref={domRef}
      className={className}
      inActivity={inActivity}
    />
  );
};

ImmersiveReaderButton.propTypes = {
  userToken: PropTypes.string,
  title: PropTypes.string,
  nodeData: PropTypes.object,
  show: PropTypes.bool,
  darkMode: PropTypes.bool,
  className: PropTypes.string,
  inActivity: PropTypes.bool,
};

export default ImmersiveReaderButton;
